import { Component, Input, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { BuildService } from 'src/app/build-data/services/build.service';

@Component({
  selector: 'app-side-thumbnail',
  templateUrl: './side-thumbnail.component.html',
  styleUrls: ['./side-thumbnail.component.css']
})
export class SideThumbnailComponent implements OnInit {
  @Input() record: any;
  @Input() imageRecord: any;
  @Input() field: any;
  @Input() customLabel: any = null;
  @Input() options: any;
  @Input() timestamp: number;
  public imgData: any = {};
  public showImgDialog: boolean = false;

  constructor(
    private clipboard: Clipboard,
    private messageService: MessageService,
    private buildService: BuildService
  ) { }

  ngOnInit(): void {
    this.getBuildData();
  }

  /** Compiles and sets global imgData, then sets showImgDialog to true.
   * @param field FieldData
   */
  displayImage(url: string) {
    // For direct paths (not thumbnails), don't try to replace _128 with _1024
    let previewPath = url;
    if (url.includes('_128.')) {
      previewPath = url.replace('_128.', '_1024.');
    }
    
    this.imgData = {
      title: this.record.name,
      imageName: this.field.label,
      name: previewPath,
      path: previewPath,
    };
    setTimeout(() => {
      this.showImgDialog = true;
    }, 300);
  }

   /**
   *
   * @param copyVal // text to copy into the user's clipboard
   */
   copyToClipboard(copyVal: string) {
    this.clipboard.copy(copyVal);
    this.messageService.add({
      life: 500,
      sticky: false,
      severity: 'success',
      summary: 'Thumbnail Copied',
      detail: `URL copied to clipboard successfully!`,
    });
  }

  /**
   * Retrieves the build data for each entity in the record.
   * If the entity has build data, it makes an API call to get the detailed build data and status.
   * If not, it sets a default build status indicating that the build data was not found.
   */
  async getBuildData() {
    await this.fetchAndUpdateBuildData(this.imageRecord);
  }

  /**
   * Fetches and updates the build data for a given entity.
   * @param {Object} entity - The entity for which to fetch and update the build data.
   */
  async fetchAndUpdateBuildData(entity: any) {
    if (entity && entity.buildData) {
      // Fetch the build data from the service.
      let response = await this.buildService.getBuildData(entity.buildData);
      // console.log('build data response:', response);

      // Update the entity with the fetched build data and status.
      entity.buildData = response?.buildData;
      entity.buildStatus = response?.buildStatus;
    } else {
      if (!entity) entity = {};
      // Set a default build status if no build data is present.
      entity.buildData = [];
      entity.buildStatus = {
        text: 'Build Status Not Found',
        date: '',
        color: 'var(--gray-400)',
        buildCount: 0,
      };
    }
  }

  /**
   * Returns style for a given record
   * @param record The record for which style needs to be computed
   */
  getStyle(record: any): any {
    if (!record || !record.buildStatus) {
      return {};
    }

    return {
      'font-size': '12px',
      position: 'relative',
      color: 'var(--primary-color-text)',
      'background-color': record.buildStatus.color || '',
      'border-radius': '0.5em'
    };
  }

  /**
   * Returns badge style for a given record
   * @param record The record for which badge style needs to be computed
   */
  getBadgeStyle(record: any): any {
    if (!record || !record.buildStatus) {
      return {};
    }

    return {
      'background-color': record.buildStatus.color || '',
      position: 'absolute',
      right: '-0.5rem',
      top: '-1em'
    };
  }

  /**
   * Gets the thumbnail path, trying different sources
   */
  getThumbnailPath(): string {
    if (!this.imageRecord) {
      return '';
    }

    // Try thumbnails array first (for other components that might have it)
    if (this.imageRecord.thumbnails && this.imageRecord.thumbnails.length > 0) {
      const indices = [1, 2, 0]; // Try different thumbnail indices
      for (const index of indices) {
        if (this.imageRecord.thumbnails[index]?.path) {
          return this.imageRecord.thumbnails[index].path;
        }
      }
      return this.imageRecord.thumbnails[0]?.path || '';
    }

    // Try individual thumbnail URL fields
    const thumbnailFields = ['thumbnail_url_128', 'thumbnail_url_256', 'thumbnail_url_64', 'thumbnail_url_32'];
    for (const field of thumbnailFields) {
      if (this.imageRecord[field]?.trim()) {
        return this.imageRecord[field];
      }
    }

    // Fallback to constructing URL from path field
    return this.imageRecord.path ? this.constructImageUrl(this.imageRecord.path) : '';
  }

  /**
   * Gets the preview path for the dialog (prefers higher resolution)
   */
  getPreviewPath(): string {
    if (!this.imageRecord) {
      return '';
    }

    // Try thumbnails array first (for other components that might have it)
    if (this.imageRecord.thumbnails && this.imageRecord.thumbnails.length > 0) {
      const indices = [0, 1, 2]; // Try different thumbnail indices for preview
      for (const index of indices) {
        if (this.imageRecord.thumbnails[index]?.path) {
          return this.imageRecord.thumbnails[index].path;
        }
      }
      return this.imageRecord.thumbnails[0]?.path || '';
    }

    // Try individual thumbnail URL fields (prefer higher resolution for preview)
    const thumbnailFields = ['thumbnail_url_1024', 'thumbnail_url_256', 'thumbnail_url_128', 'thumbnail_url_64', 'thumbnail_url_32'];
    for (const field of thumbnailFields) {
      if (this.imageRecord[field]?.trim()) {
        return this.imageRecord[field];
      }
    }

    // Fallback to constructing URL from path field
    return this.imageRecord.path ? this.constructImageUrl(this.imageRecord.path) : '';
  }

  /**
   * Constructs the full image URL from the path
   */
  constructImageUrl(path: string): string {
    if (!path) {
      return '';
    }

    // If the path already starts with http/https, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    // Construct the full URL using the CloudFront base URL
    const baseUrl = 'https://d3tfb94dc03jqa.cloudfront.net/2021/';
    
    // Remove leading slash from path if it exists
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    
    // Extract the directory and filename without extension
    const pathParts = cleanPath.split('/');
    const filename = pathParts[pathParts.length - 1];
    const filenameWithoutExt = filename.replace(/\.[^/.]+$/, ''); // Remove extension
    
    // Construct the directory path (everything except the filename)
    const directoryPath = pathParts.slice(0, -1).join('/');
    
    // Build the full path with the directory structure
    const fullPath = `${directoryPath}/${filenameWithoutExt}/${filenameWithoutExt}_256.png`;
    
    return baseUrl + fullPath;
  }

}
